/* eslint-disable @typescript-eslint/no-explicit-any */
import { computed, signal, Signal, WritableSignal } from '@angular/core';

export abstract class BaseSignalStore<TState extends BaseState> {
  // Private writable signal
  protected readonly _state: WritableSignal<TState>;

  // Public readonly signals for each state property
  public readonly isLoading: Signal<boolean>;
  public readonly error: Signal<string | null>;

  constructor(initialState: TState) {
    // Initialize store state
    this._state = signal(initialState);

    // Create computed signals for base properties
    this.isLoading = computed(() => this._state().isLoading);
    this.error = computed(() => this._state().error);
  }

  // Selector method for creating custom computed signals
  public select<K>(selector: (state: TState) => K): Signal<K> {
    return computed(() => selector(this.state()));
  }

  // Protected method to update state immutably
  protected patchState(partial: Partial<TState>): void {
    this._state.update((current) => ({ ...current, ...partial }));
  }

  // Protected method to update state with function
  protected updateState(updateFn: (state: TState) => TState): void {
    this._state.update(updateFn);
  }

  // Protected method to get current state value
  public state(): TState {
    return this._state();
  }

  // Public method to reset state
  public reset(initialState: TState): void {
    this._state.set(initialState);
  }

  // Base methods for loading and error states
  public setLoading(isLoading: boolean): void {
    this.patchState({ isLoading } as Partial<TState>);
  }

  protected setError(error: string | null): void {
    this.patchState({ error } as Partial<TState>);
  }

  // Helper for async operations
  protected async executeAsync<T>(
    operation: () => Promise<T>,
    onSuccess?: (result: T) => void,
    onError?: (error: any) => void
  ): Promise<T | void> {
    this.setLoading(true);
    this.setError(null);

    try {
      const result = await operation();
      onSuccess?.(result);
      return result;
    } catch (error: any) {
      const errorMessage = error?.message || 'An error occurred';
      this.setError(errorMessage);
      onError?.(error);
    } finally {
      this.setLoading(false);
    }
  }
}

// Base state interface
export interface BaseState {
  isLoading: boolean;
  error: string | null;
}
